import {
	ArrowRightIcon,
	ArrowUpIcon,
	FalqonsIcon,
	InstagramIcon,
	LinkedinIcon,
	LogoIcon,
	TiktokIcon,
} from "@assets/icons";
import Input from "@components/common/Input/Input";
import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { smoothScrollToTop } from "@utils/smoothScroll";
import { ROUTES } from "constants/routes";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { subscribeNewsletter } from "services/newsletterService";
import { useTranslation } from "react-i18next";

const Footer: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [email, setEmail] = useState("");

	const handleScrolltoTop = () => {
		smoothScrollToTop();
	};

	const handleNewsletterSubmit = async () => {
		const trimmedEmail = email.trim().toLowerCase();
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (!emailRegex.test(trimmedEmail)) {
			toast.error(t("footer.invalidEmail"));
			return;
		}

		const allowedDomains = ["gmail.com", "mail.ru", "outlook.com"];
		const emailDomain = trimmedEmail.split("@")[1];

		if (!allowedDomains.includes(emailDomain)) {
			toast.error(t("footer.notAllowedEmail"));
			return;
		}

		try {
			await subscribeNewsletter(trimmedEmail);
			toast.success(t("footer.success"));
			setEmail("");
		} catch {
			toast.error(t("footer.error"));
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleNewsletterSubmit();
		}
	};

	return (
		<footer className="grid grid-cols-1 grid-rows-maxTwo">
			<div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-8 minPhone:px-6 minPhone:py-4 xl:px-16 xl:py-10 bg-shadeTwo">
				<div className="grid grid-cols-maxTwo place-content-between items-center gap-4">
					<SvgIcon Icon={LogoIcon} className="lg:w-[170px] lg:h-[50px]" />
					<div
						className="grid grid-cols-maxTwo items-center gap-2 cursor-pointer"
						onClick={handleScrolltoTop}
					>
						<P
							size="p2"
							type="semibold"
							className="text-mainColor cursor-pointer"
						>
							{t("footer.toTop")}
						</P>
						<SvgIcon Icon={ArrowUpIcon} />
					</div>
				</div>
				<div className="grid minPhone:grid-cols-1 xl:grid-cols-maxTwo place-content-between items-center gap-4">
					<div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-8">
						<div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-2">
							<P size="p1" type="semibold" className="text-mainColor">
								{t("footer.newsletterTitle")}
							</P>
							<P size="p3" type="regular" className="text-mainColor">
								{t("footer.newsletterSubtitle")}
							</P>

							<Input
								type="email"
								placeholder="e-mail"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								onKeyDown={handleKeyDown}
								rightIcon={
									<div
										className="p-1 rounded-md bg-shadeTwo cursor-pointer"
										onClick={handleNewsletterSubmit}
									>
										<SvgIcon Icon={ArrowRightIcon} />
									</div>
								}
							/>
						</div>

						<div
							onClick={() => window.open("https://falqone.com", "_blank")}
							className="cursor-pointer"
						>
							<SvgIcon Icon={FalqonsIcon} />
						</div>
					</div>
					<div className="grid minPhone:grid-cols-1 xl:grid-cols-maxTwo gap-8">
						<div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-3">
							<P
								size="p3"
								type="semibold"
								className="cursor-pointer text-mainColor"
							>
								{t("footer.sections")}
							</P>
							<P
								size="p3"
								type="regular"
								className="cursor-pointer text-mainColor"
								onClick={() => navigate(ROUTES.PRODUCTS)}
							>
								{t("navbar.products")}
							</P>
							<P
								size="p3"
								type="regular"
								className="cursor-pointer text-mainColor"
								onClick={() => navigate(ROUTES.ABOUT)}
							>
								{t("navbar.about")}
							</P>
							<P
								size="p3"
								type="regular"
								className="cursor-pointer text-mainColor"
								onClick={() => navigate(ROUTES.FAQ)}
							>
								{t("navbar.faq")}
							</P>
							<P
								size="p3"
								type="regular"
								className="cursor-pointer text-mainColor"
								onClick={() => navigate(ROUTES.CONTACT)}
							>
								{t("navbar.contact")}
							</P>
						</div>
						<div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-3">
							<P
								size="p3"
								type="semibold"
								className="cursor-pointer text-mainColor"
							>
								{t("footer.contact")}
							</P>
							<P
								size="p3"
								type="regular"
								className="cursor-pointer text-mainColor"
							>
								+90 533 558 4537
							</P>
							<P
								size="p3"
								type="regular"
								className="cursor-pointer text-mainColor"
							>
								g.nuryagdi@falqone.com
							</P>
							<P
								size="p3"
								type="regular"
								className="text-mainColor"
								dangerouslySetInnerHTML={{ __html: t("footer.address") }}
							/>
						</div>
					</div>
				</div>
			</div>
			<div className="grid minPhone:grid-cols-1 xl:grid-cols-maxTwo items-center place-content-between minPhone:px-6 minPhone:py-4 minPhone:gap-4 xl:px-16 xl:py-6 bg-mainColor">
				<P size="t1" type="regular" className="text-shadeTwo">
					{t("footer.copyright")}
				</P>
				<div className="grid grid-cols-1 items-center gap-0">
					{/* <a
						href="https://www.instagram.com/yourprofile"
						target="_blank"
						rel="noopener noreferrer"
						className="p-2 rounded-lg bg-shadeTwo cursor-pointer"
					>
						<SvgIcon Icon={InstagramIcon} />
					</a> */}
					<a
						href="https://www.linkedin.com/in/gadyr"
						target="_blank"
						rel="noopener noreferrer"
						className="p-2 rounded-lg bg-shadeTwo cursor-pointer"
					>
						<SvgIcon Icon={LinkedinIcon} />
					</a>
					{/* <a
						href="https://www.tiktok.com/@yourprofile"
						target="_blank"
						rel="noopener noreferrer"
						className="p-2 rounded-lg bg-shadeTwo cursor-pointer"
					>
						<SvgIcon Icon={TiktokIcon} />
					</a> */}
				</div>
			</div>
		</footer>
	);
};

export default Footer;
